import React, { useEffect, useCallback, useRef } from 'react';
import type { UserProfile, ConnectedRepository, RecentAnalysis } from '../types/dashboard';
import { guidanceStateManager } from '../utils/guidanceStateManager';
import { logger } from '../utils/logger';

export type OnboardingReason =
  | 'first-visit'
  | 'no-repositories'
  | 'no-analyses'
  | 'manual-request'
  | 'returning-user';

export interface OnboardingDetectorProps {
  user: UserProfile;
  repositories: ConnectedRepository[];
  analyses: RecentAnalysis[];
  onGuidanceNeeded: (reason: OnboardingReason) => void;
  onGuidanceNotNeeded?: () => void;
  checkInterval?: number; // milliseconds
  enabled?: boolean;
}

export interface OnboardingCriteria {
  maxAccountAge: number; // hours
  minRepositories: number;
  minAnalyses: number;
  maxInactivityDays: number;
}

const DEFAULT_CRITERIA: OnboardingCriteria = {
  maxAccountAge: 24, // 24 hours
  minRepositories: 1,
  minAnalyses: 1,
  maxInactivityDays: 30,
};

export const OnboardingDetector: React.FC<OnboardingDetectorProps> = ({
  user,
  repositories,
  analyses,
  onGuidanceNeeded,
  onGuidanceNotNeeded,
  checkInterval = 5000, // Check every 5 seconds
  enabled = true,
}) => {
  const lastCheckRef = useRef<OnboardingReason | null>(null);
  const intervalRef = useRef<number | null>(null);

  const detectOnboardingNeed = useCallback((): OnboardingReason | null => {
    if (!enabled || !user) {
      return null;
    }

    try {
      const now = new Date();
      const accountAge = now.getTime() - user.joinedAt.getTime();
      const accountAgeHours = accountAge / (1000 * 60 * 60);

      // Get current guidance state
      const guidanceState = guidanceStateManager.getState();

      // Check if user has explicitly completed guidance or dismissed it
      if (user.guidanceCompleted || (guidanceState && guidanceState.dismissed)) {
        return null;
      }

      // Check if guidance is currently active - don't interfere with active guidance
      if (guidanceState && guidanceState.enabled) {
        return null;
      }

      // Check if guidance has been fully completed before (prevent repeated auto-shows)
      // Only check completion count, not steps completed, since steps increment during active guidance
      if (guidanceState && guidanceState.interactions.completionCount > 0) {
        return null;
      }

      // Only show automatic guidance for truly first-time users
      // 1. First-time user detection (account age < 24 hours AND no previous guidance interaction)
      if (accountAgeHours < DEFAULT_CRITERIA.maxAccountAge && user.isFirstVisit) {
        return 'first-visit';
      }

      // For other cases, don't automatically show guidance - user can use the manual button
      return null;
    } catch (error) {
      logger.error('[OnboardingDetector] Error detecting onboarding need', { error });
      return null;
    }
  }, [user, enabled]);

  const performCheck = useCallback(() => {
    const reason = detectOnboardingNeed();

    // Get current guidance state to check if guidance is active
    const guidanceState = guidanceStateManager.getState();
    const isGuidanceActive = guidanceState && guidanceState.enabled;

    // Only trigger if the reason has changed AND guidance is not currently active
    if (reason !== lastCheckRef.current) {
      lastCheckRef.current = reason;

      if (reason) {
        onGuidanceNeeded(reason);
      } else if (!isGuidanceActive) {
        // Only call onGuidanceNotNeeded if guidance is not currently active
        // This prevents closing active guidance sessions
        if (onGuidanceNotNeeded) {
          onGuidanceNotNeeded();
        }
      }
    }
  }, [detectOnboardingNeed, onGuidanceNeeded, onGuidanceNotNeeded]);

  // Initial check on mount and when dependencies change
  useEffect(() => {
    if (enabled) {
      // Don't perform check if guidance is currently active
      const guidanceState = guidanceStateManager.getState();
      if (!guidanceState || !guidanceState.enabled) {
        performCheck();
      }
    }
  }, [enabled, user, repositories, analyses, performCheck]);

  // Set up periodic checking
  useEffect(() => {
    if (!enabled || checkInterval <= 0) {
      return;
    }

    intervalRef.current = window.setInterval(() => {
      // Don't perform periodic checks if guidance is currently active
      const guidanceState = guidanceStateManager.getState();
      if (!guidanceState || !guidanceState.enabled) {
        performCheck();
      }
    }, checkInterval);

    return () => {
      if (intervalRef.current) {
        window.clearInterval(intervalRef.current);
      }
    };
  }, [enabled, checkInterval, performCheck]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        window.clearInterval(intervalRef.current);
      }
    };
  }, []);

  // This component doesn't render anything
  return null;
};

export default OnboardingDetector;
